<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from "vue";
import { HISTORY_SCROLL_ICON_SVG } from "@/components/historyScrollIcon";

const props = defineProps<{
  visible: boolean;
  /** 数据就绪后由父级置为 true，触发卷轴收起动画（替代直接淡出） */
  closing: boolean;
}>();

const emit = defineEmits<{
  loaded: [];
  error: [];
  /** 收起动画（含飞向导航栏图标的令牌）播放完毕 */
  closed: [];
}>();

const viewportRef = ref<HTMLElement | null>(null);
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const ROLL_UP_DURATION = 880;
const FLIGHT_DURATION = 560;
const FLIGHT_SIZE = 64;
let closeTimer: number | undefined;

watch(
  () => props.closing,
  (value) => {
    if (value) startCloseSequence();
  }
);

function startCloseSequence(): void {
  if (reducedMotion) {
    // 减弱动态效果：保留简单淡出
    closeTimer = window.setTimeout(() => emit("closed"), 420);
    return;
  }
  closeTimer = window.setTimeout(launchFlightToken, ROLL_UP_DURATION);
}

/** 收起完成后，生成一枚小卷轴令牌飞向导航栏的卷轴入口图标 */
function launchFlightToken(): void {
  const target = document.querySelector<HTMLElement>(".history-scroll-entry");
  const viewport = viewportRef.value;
  if (!target || !viewport) {
    emit("closed");
    return;
  }
  const from = viewport.getBoundingClientRect();
  const to = target.getBoundingClientRect();
  const startX = from.left + from.width / 2 - FLIGHT_SIZE / 2;
  const startY = from.top + from.height / 2 - FLIGHT_SIZE / 2;
  const endX = to.left + to.width / 2 - FLIGHT_SIZE / 2;
  const endY = to.top + to.height / 2 - FLIGHT_SIZE / 2;

  const token = document.createElement("div");
  token.className = "history-scroll-flight";
  token.innerHTML = HISTORY_SCROLL_ICON_SVG;
  token.style.transform = `translate(${startX}px, ${startY}px) scale(0.86)`;
  document.body.appendChild(token);

  const animation = token.animate(
    [
      { transform: `translate(${startX}px, ${startY}px) scale(0.86)`, opacity: 1, offset: 0 },
      {
        transform: `translate(${startX}px, ${startY}px) scale(1)`,
        opacity: 1,
        offset: 0.18
      },
      {
        transform: `translate(${endX}px, ${endY}px) scale(0.52)`,
        opacity: 0.95,
        offset: 1
      }
    ],
    { duration: FLIGHT_DURATION, easing: "cubic-bezier(0.45, 0, 0.2, 1)", fill: "forwards" }
  );
  animation.onfinish = () => {
    token.remove();
    emit("closed");
  };
}

onBeforeUnmount(() => {
  if (closeTimer !== undefined) window.clearTimeout(closeTimer);
  document.querySelectorAll(".history-scroll-flight").forEach((el) => el.remove());
});
</script>

<template>
  <section
    v-if="visible"
    class="initial-history-loading"
    :class="{ 'is-closing': closing, 'is-reduced': reducedMotion }"
    aria-busy="true"
    aria-label="正在展开江城记忆时间轴"
  >
    <div ref="viewportRef" class="initial-history-loading__viewport">
      <div class="initial-history-loading__track">
        <img
          class="initial-history-loading__image"
          src="/initial-history-scroll.webp"
          alt=""
          @load="emit('loaded')"
          @error="emit('error')"
        />
        <img
          class="initial-history-loading__image"
          src="/initial-history-scroll.webp"
          alt=""
          aria-hidden="true"
        />
      </div>
    </div>
  </section>
</template>

<style scoped>
.initial-history-loading {
  position: fixed;
  z-index: 6000;
  inset: 0;
  display: flex;
  align-items: center;
  overflow: hidden;
  background: #0a201d;
  pointer-events: all;
  transition: background 720ms ease;
}

.initial-history-loading.is-closing {
  background: rgba(10, 32, 29, 0);
  pointer-events: none;
}

.initial-history-loading.is-closing.is-reduced {
  opacity: 0;
  transition: background 0s, opacity 400ms ease;
}

.initial-history-loading::before,
.initial-history-loading::after {
  position: absolute;
  z-index: 1;
  top: 0;
  bottom: 0;
  width: clamp(2.5rem, 8vw, 9rem);
  content: "";
  pointer-events: none;
  transition: opacity 520ms ease;
}

.initial-history-loading.is-closing::before,
.initial-history-loading.is-closing::after {
  opacity: 0;
}

.initial-history-loading::before {
  left: 0;
  background: linear-gradient(90deg, #0a201d 0%, rgba(10, 32, 29, 0) 100%);
}

.initial-history-loading::after {
  right: 0;
  background: linear-gradient(270deg, #0a201d 0%, rgba(10, 32, 29, 0) 100%);
}

.initial-history-loading__viewport {
  width: 100%;
  overflow: hidden;
  clip-path: inset(0 0 0 0);
}

.initial-history-loading.is-closing .initial-history-loading__viewport {
  clip-path: inset(0 50% 0 50%);
  opacity: 0;
  transition:
    clip-path 860ms cubic-bezier(0.6, 0.02, 0.35, 1),
    opacity 180ms ease 700ms;
}

.initial-history-loading__track {
  display: flex;
  width: max-content;
  height: clamp(15rem, 58vh, 42rem);
  animation: history-scroll 18s linear infinite;
  will-change: transform;
}

.initial-history-loading__image {
  display: block;
  width: auto;
  height: 100%;
  max-width: none;
  user-select: none;
}

@keyframes history-scroll {
  from {
    transform: translateX(calc(-100% + 100vw));
  }

  to {
    transform: translateX(calc(-50% + 100vw));
  }
}

@media (max-width: 720px) {
  .initial-history-loading__track {
    height: clamp(13rem, 46vh, 22rem);
    animation-duration: 14s;
  }
}

@media (prefers-reduced-motion: reduce) {
  .initial-history-loading__track {
    animation: none;
    transform: translateX(calc(-100% + 100vw));
  }
}
</style>
